/**
 * The user-driven recovery actions of the shared preference-order controller
 * (SE-8389): Retry / Discard for a failed save, Reload / Re-apply for a
 * stale-version rejection. Moved byte-for-byte out of
 * `useWatchlistPreferenceOrder.ts` (SE-8391) so the former-slot memory fits
 * in the hook; the request bookkeeping stays where it was.
 * @module features/scouting/hooks/useWatchlistPreferenceRecovery
 */
import { type Dispatch, type RefObject, useCallback, useRef } from "react";

import type { WatchlistPreferenceOrderAction } from "./watchlistPreferenceOrderReducer";
import type { PitchWriteExecutor } from "./watchlistPreferenceOrderQueue";
import type {
  WatchlistPitchOperation,
  WatchlistPreferenceOrderState,
} from "./watchlistPreferenceOrderState";

/** What the controller lends the recovery actions. */
export interface UseWatchlistPreferenceRecoveryOptions {
  readonly watchlistId: string | null;
  readonly projectedState: WatchlistPreferenceOrderState;
  readonly dispatch: Dispatch<WatchlistPreferenceOrderAction>;
  readonly runPitchWrite: (
    targetWatchlistId: string,
    operation: WatchlistPitchOperation
  ) => Promise<void>;
  readonly settlePending: (
    targetWatchlistId: string,
    operationId: string,
    through: boolean
  ) => void;
  readonly executors: RefObject<Map<string, PitchWriteExecutor>>;
  readonly pendingOperations: RefObject<Map<string, WatchlistPitchOperation[]>>;
  /** Marks the held copy as the one re-apply in flight. */
  readonly markReapplying: (operationId: string) => void;
}

/** The recovery actions and the operations they act on. */
export interface WatchlistPreferenceRecovery {
  readonly failedOperation?: WatchlistPitchOperation;
  readonly heldChange?: WatchlistPitchOperation;
  readonly retry: () => Promise<void>;
  readonly discard: () => void;
  readonly reload: () => void;
  readonly reapply: () => Promise<void>;
}

/**
 * Binds Retry / Discard / Reload / Re-apply to the controller's queue.
 * @param options - The controller's state, dispatcher and request bookkeeping
 * @param options.watchlistId - Selected watchlist identity
 * @param options.projectedState - The state the actions read
 * @param options.dispatch - The reducer dispatcher
 * @param options.runPitchWrite - Re-runs one operation's request
 * @param options.settlePending - Removes a settled operation from the in-flight list
 * @param options.executors - The request behind each operation
 * @param options.pendingOperations - In-flight operations per watchlist
 * @param options.markReapplying - Marks the held copy as the re-apply in flight
 * @returns The bound actions
 */
export const useWatchlistPreferenceRecovery = ({
  watchlistId,
  projectedState,
  dispatch,
  runPitchWrite,
  settlePending,
  executors,
  pendingOperations,
  markReapplying,
}: UseWatchlistPreferenceRecoveryOptions): WatchlistPreferenceRecovery => {
  // The queue holds one object per operation, so `find` is identity-stable.
  const failedOperation = projectedState.operations.find(
    operation => operation.id === projectedState.failure?.operationId
  );
  // A second press before the `saving` rerender would enqueue the same
  // operation twice — the queue serialises writes, it does not dedupe them —
  // so a recovery request is refused while its predecessor is still in flight.
  const inFlight = useRef(new Set<string>());
  const runOnce = useCallback(
    async (targetWatchlistId: string, operation: WatchlistPitchOperation) => {
      inFlight.current.add(operation.id);
      try {
        await runPitchWrite(targetWatchlistId, operation);
      } finally {
        inFlight.current.delete(operation.id);
      }
    },
    [runPitchWrite]
  );
  const retry = useCallback(async () => {
    if (!watchlistId || !failedOperation) return;
    if (inFlight.current.has(failedOperation.id)) return;
    dispatch({ type: "retry", watchlistId, operationId: failedOperation.id });
    await runOnce(watchlistId, failedOperation);
  }, [dispatch, failedOperation, runOnce, watchlistId]);
  const discard = useCallback(() => {
    if (!watchlistId || !failedOperation) return;
    executors.current.delete(failedOperation.id);
    settlePending(watchlistId, failedOperation.id, false);
    dispatch({ type: "discard", watchlistId, operationId: failedOperation.id });
  }, [dispatch, executors, failedOperation, settlePending, watchlistId]);
  const heldChange = projectedState.heldChange;
  const reload = useCallback(() => {
    if (!watchlistId) return;
    if (heldChange) executors.current.delete(heldChange.id);
    dispatch({ type: "reload", watchlistId });
  }, [dispatch, executors, heldChange, watchlistId]);
  const reapply = useCallback(async () => {
    if (!watchlistId || !heldChange) return;
    if (inFlight.current.has(heldChange.id)) return;
    markReapplying(heldChange.id);
    pendingOperations.current.set(watchlistId, [
      ...(pendingOperations.current.get(watchlistId) ?? []),
      heldChange,
    ]);
    dispatch({ type: "reapply", watchlistId });
    await runOnce(watchlistId, heldChange);
  }, [
    dispatch,
    heldChange,
    pendingOperations,
    markReapplying,
    runOnce,
    watchlistId,
  ]);
  return { failedOperation, heldChange, retry, discard, reload, reapply };
};
